import { EnhancedBaseService } from '../base/EnhancedBaseService';
import type { QueryOptions, ServiceResponse } from '../base/ServiceTypes';

// Auto responder configuration structure
export interface AutoResponderConfig {
  id?: string;
  user_id: string;
  enabled: boolean;
  response_delay_seconds: number;
  escalation_threshold: number;
  active_platforms: string[];
  engagement_enabled?: boolean;
  poll_interval_seconds?: number;
  auto_categorize?: boolean;
  hide_spam_auto?: boolean;
  created_at?: string;
  updated_at?: string;
}

export type AutoResponderConfigInsert = Omit<
  AutoResponderConfig,
  'id' | 'created_at' | 'updated_at'
> & {
  created_at?: string;
  updated_at?: string;
};

export type AutoResponderConfigUpdate = Partial<
  Omit<AutoResponderConfig, 'id' | 'user_id' | 'created_at'>
> & {
  updated_at?: string;
};

// Response template structure
export interface ResponseTemplate {
  id?: string;
  user_id: string;
  template_name: string;
  template_text: string;
  template_type: string;
  variables: string[];
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export type ResponseTemplateInsert = Omit<ResponseTemplate, 'id' | 'created_at' | 'updated_at'> & {
  created_at?: string;
  updated_at?: string;
};

export type ResponseTemplateUpdate = Partial<
  Omit<ResponseTemplate, 'id' | 'user_id' | 'created_at'>
> & {
  updated_at?: string;
};

// Default configuration
const DEFAULT_CONFIG: Omit<AutoResponderConfig, 'id' | 'user_id' | 'created_at' | 'updated_at'> = {
  enabled: false,
  response_delay_seconds: 30,
  escalation_threshold: 0.7,
  active_platforms: [],
};

/**
 * Service for managing auto-responder configuration and templates
 * Handles configuration, response templates, and analytics
 */
export class AutoResponderService extends EnhancedBaseService {
  private static instance: AutoResponderService;

  public static getInstance(): AutoResponderService {
    if (!AutoResponderService.instance) {
      AutoResponderService.instance = new AutoResponderService({
        deduplication: { enabled: true, ttl: 10000 },
        smartCaching: { enabled: true },
      });
    }
    return AutoResponderService.instance;
  }

  /**
   * Shared-account RLS: auto_responder_config/queue/cache/rate_limits and
   * response_templates all key on current_account_owner_id(), not the raw
   * session user. Resolve it once and use it for every read/write below so a
   * team member sees the account owner's config instead of an empty/failing
   * per-member scope. Fails closed on both an RPC error AND a non-string
   * response — previously the non-string case silently fell back to the raw
   * `userId`, an inconsistency with the RPC-error branch's own fail-closed
   * behavior (fixed 2026-08-15; `_userId` kept for call-site signature
   * stability even though it's no longer used as a fallback value).
   */
  private async resolveOwnerId(_userId: string): Promise<string> {
    const { data: ownerIdData, error: ownerIdError } = await this.supabase.rpc(
      'current_account_owner_id',
    );
    if (ownerIdError) {
      throw this.createError(
        `Account owner lookup failed: ${ownerIdError.message}`,
        'QUERY_ERROR',
        ownerIdError,
      );
    }
    if (typeof ownerIdData !== 'string' || !ownerIdData) {
      throw this.createError('Account owner lookup returned no owner id', 'QUERY_ERROR');
    }
    return ownerIdData;
  }

  /**
   * Get auto-responder configuration for the current user
   */
  async getConfig(options?: QueryOptions): Promise<ServiceResponse<AutoResponderConfig | null>> {
    return this.executeWithDedup(
      async () => {
        const userId = await this.ensureAuthenticated();
        const ownerId = await this.resolveOwnerId(userId);
        const cacheKey = `auto-responder-config-${ownerId}`;

        // Check cache first
        const cached = this.getFromCache<AutoResponderConfig>(cacheKey);
        if (cached && !options?.skipCache) {
          return cached;
        }

        const { data, error } = await this.supabase
          .from('auto_responder_config')
          .select('*')
          .eq('user_id', ownerId)
          .maybeSingle();

        if (error) {
          throw this.createError(error.message, 'QUERY_ERROR', error);
        }

        // If no config exists, create default
        if (!data) {
          const defaultConfig = await this.createDefaultConfig();
          if (defaultConfig.success) {
            this.setCache(cacheKey, defaultConfig.data);
            return defaultConfig.data;
          }
          return null;
        }

        this.setCache(cacheKey, data);
        return data;
      },
      `get-config-${await this.ensureAuthenticated()}`,
    );
  }

  /**
   * Update auto-responder configuration
   */
  async updateConfig(
    updates: AutoResponderConfigUpdate,
  ): Promise<ServiceResponse<AutoResponderConfig>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const ownerId = await this.resolveOwnerId(userId);

      const { data, error } = await this.supabase
        .from('auto_responder_config')
        .update(updates)
        .eq('user_id', ownerId)
        .select()
        .single();

      if (error) {
        throw this.createError(error.message, 'UPDATE_ERROR', error);
      }

      // Clear cache
      this.clearCache(`auto-responder-config-${ownerId}`);

      return data;
    });
  }

  /**
   * Create default configuration for new users
   */
  private async createDefaultConfig(): Promise<ServiceResponse<AutoResponderConfig>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const ownerId = await this.resolveOwnerId(userId);

      const configData: AutoResponderConfigInsert = {
        ...DEFAULT_CONFIG,
        user_id: ownerId,
      };

      const { data, error } = await this.supabase
        .from('auto_responder_config')
        .insert(configData)
        .select()
        .single();

      if (error) {
        throw this.createError(error.message, 'INSERT_ERROR', error);
      }

      return data;
    });
  }

  /**
   * Get response templates for the current user
   */
  async getTemplates(options?: QueryOptions): Promise<ServiceResponse<ResponseTemplate[]>> {
    return this.executeWithDedup(
      async () => {
        const userId = await this.ensureAuthenticated();
        const ownerId = await this.resolveOwnerId(userId);
        const cacheKey = `response-templates-${ownerId}`;

        // Check cache first
        const cached = this.getFromCache<ResponseTemplate[]>(cacheKey);
        if (cached && !options?.skipCache) {
          return cached;
        }

        const { data, error } = await this.supabase
          .from('response_templates')
          .select('*, template_type, template_name, variables')
          .or(`user_id.eq.${ownerId},user_id.is.null`)
          .order('template_type', { ascending: true });

        if (error) {
          throw this.createError(error.message, 'QUERY_ERROR', error);
        }

        // Map database columns to expected frontend structure
        const templates = (data || []).map((template) => ({
          ...template,
          category: template.template_type,
          name: template.template_name,
          placeholders: template.variables || [],
        })) as ResponseTemplate[];

        this.setCache(cacheKey, templates);
        return templates;
      },
      `get-templates-${await this.ensureAuthenticated()}`,
    );
  }

  /**
   * Update a response template
   */
  async updateTemplate(
    id: string,
    updates: ResponseTemplateUpdate,
  ): Promise<ServiceResponse<ResponseTemplate>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const ownerId = await this.resolveOwnerId(userId);

      const { data, error } = await this.supabase
        .from('response_templates')
        .update(updates)
        .eq('id', id)
        .eq('user_id', ownerId)
        .select()
        .single();

      if (error) {
        throw this.createError(error.message, 'UPDATE_ERROR', error);
      }

      // Clear cache
      this.clearCache(`response-templates-${ownerId}`);

      return data;
    });
  }

  /**
   * Enable or disable auto-responder for a user
   */
  async toggleEnabled(enabled: boolean): Promise<ServiceResponse<AutoResponderConfig>> {
    return this.updateConfig({ enabled });
  }

  /**
   * Update active platforms for auto-responder
   */
  async updateActivePlatforms(platforms: string[]): Promise<ServiceResponse<AutoResponderConfig>> {
    return this.updateConfig({ active_platforms: platforms });
  }

  /**
   * Clear all cached data for auto-responder
   */
  clearAllCache(): void {
    // This method would be called when user logs out or data needs refresh
    this.clearCachePattern('auto-responder-*');
    this.clearCachePattern('response-templates-*');
  }
}

// Create and export singleton instance
export const autoResponderService = AutoResponderService.getInstance();
